import { apiService } from './api';

const http = apiService.client;

export type MapLayerCatalogEntry = {
  schema: string;
  table: string;
  geomColumn: string;
  geomType: string;
  coordDimension: number;
  srid: number;
};

export type GeoJsonFeatureCollection = {
  type: 'FeatureCollection';
  features: Array<{ type: 'Feature'; geometry: GeoJSON.Geometry; properties: Record<string, unknown> }>;
};

export type ColumnInfo = {
  name: string;
  type: string;
  udtName: string;
};

export type DbTarget = 'app' | 'sandbox';

export async function fetchMapLayerCatalog(db?: DbTarget): Promise<MapLayerCatalogEntry[]> {
  const params: Record<string, string> = {};
  if (db === 'sandbox') params.db = 'sandbox';
  const { data } = await http.get<{ layers: MapLayerCatalogEntry[] }>('/map-layers/catalog', { params });
  return data.layers ?? [];
}

export async function fetchLayerGeoJson(
  schema: string,
  table: string,
  geomColumn: string,
  limit = 2000,
  db?: DbTarget
): Promise<GeoJsonFeatureCollection> {
  const params: Record<string, string | number> = { schema, table, geomColumn, limit };
  if (db === 'sandbox') params.db = 'sandbox';
  const { data } = await http.get<GeoJsonFeatureCollection>('/map-layers/geojson', { params });
  return data;
}

export async function fetchLayerColumns(
  schema: string,
  table: string,
  db?: DbTarget
): Promise<ColumnInfo[]> {
  const params: Record<string, string> = { schema, table };
  if (db === 'sandbox') params.db = 'sandbox';
  const { data } = await http.get<{ columns: ColumnInfo[] }>('/map-layers/columns', { params });
  return data.columns ?? [];
}

const GEOM_FIELD_NAMES = new Set(['geom', 'geometry', 'the_geom', 'shape', 'ogc_fid', 'gid']);

/**
 * Infer a ColumnInfo[] schema by scanning the properties of loaded GeoJSON features.
 * Used as a fallback when `/map-layers/columns` is unavailable (e.g. source-catalog
 * virtual layers whose features come from server-side to_jsonb expansion) or returns
 * empty, so the Dashboard Builder field pickers can still offer selections.
 */
export function inferColumnsFromFeatures(
  features: Array<{ properties?: Record<string, unknown> | null }>
): ColumnInfo[] {
  const seen = new Map<string, { numeric: number; bool: number; date: number; other: number }>();

  for (const f of features) {
    const props = f?.properties;
    if (!props || typeof props !== 'object') continue;
    for (const key of Object.keys(props)) {
      if (GEOM_FIELD_NAMES.has(key)) continue;
      const v = (props as Record<string, unknown>)[key];
      if (v == null) {
        if (!seen.has(key)) seen.set(key, { numeric: 0, bool: 0, date: 0, other: 0 });
        continue;
      }
      const bucket = seen.get(key) ?? { numeric: 0, bool: 0, date: 0, other: 0 };
      if (typeof v === 'number') {
        bucket.numeric += 1;
      } else if (typeof v === 'boolean') {
        bucket.bool += 1;
      } else if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/.test(v)) {
        bucket.date += 1;
      } else {
        bucket.other += 1;
      }
      seen.set(key, bucket);
    }
  }

  const columns: ColumnInfo[] = [];
  seen.forEach((b, name) => {
    const total = b.numeric + b.bool + b.date + b.other;
    let type = 'text';
    let udt = 'text';
    if (total > 0) {
      const max = Math.max(b.numeric, b.bool, b.date, b.other);
      if (max === b.numeric) {
        type = 'double precision';
        udt = 'float8';
      } else if (max === b.bool) {
        type = 'boolean';
        udt = 'bool';
      } else if (max === b.date) {
        type = 'timestamp without time zone';
        udt = 'timestamp';
      }
    }
    columns.push({ name, type, udtName: udt });
  });

  columns.sort((a, b) => a.name.localeCompare(b.name));
  return columns;
}
